import React, { Component, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useToaster } from 'context/Toaster/ToasterContext';
import { VARIANT_COLOR_ENUM } from 'types/utils';
import { datadogRum } from '@datadog/browser-rum';

interface ErrorBoundaryState {
  hasError: boolean;
}

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryWithHooksProps extends ErrorBoundaryProps {
  addToaster: (params: { variant: VARIANT_COLOR_ENUM; message: string }) => void;
  t: (key: string) => string;
}

class ErrorBoundary extends Component<ErrorBoundaryWithHooksProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryWithHooksProps) {
    super(props);

    // Define a state variable to track whether is an error or not
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    const { addToaster, t } = this.props;

    // Display global error
    addToaster({
      variant: VARIANT_COLOR_ENUM.ERROR,
      message: t('error-boundary.title'),
    });

    // Send error to Datadog
    console.error({ error, errorInfo });
    datadogRum.addError(error, {
      errorInfo,
    });
  }

  render(): ReactNode {
    const { hasError } = this.state;
    const { children } = this.props;

    if (hasError) {
      return null;
    }

    // Return children components in case of no error
    return children;
  }
}

// Wrapper component to access hooks
const ErrorBoundaryWrapper = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  const { addToaster } = useToaster();

  return (
    <ErrorBoundary addToaster={addToaster} t={t}>
      {children}
    </ErrorBoundary>
  );
};

export default ErrorBoundaryWrapper;
